import { Icon } from '@/component/atoms/Icon'
import { Loader } from '@/component/atoms/Loader'
import type { TeacherBrand } from '@/component/molecules/SubjectSwitch/subjectMenu'
import { getSubjectSymbol } from '@/component/molecules/SubjectSwitch/subjectMenu'

import { SERVICE_ENTRY_COPY } from './serviceEntryCopy'
import S from './ServiceEntryModals.style'

type BrandProps = { brand: TeacherBrand }

const EnglishflatMark = ({ brand }: BrandProps) => {
  const symbol = getSubjectSymbol('ENGLISHFLAT', brand)
  const isTinted = symbol.kind === 'tinted'

  return (
    <span
      css={[S.mark, isTinted && S.markTinted]}
      style={isTinted ? { backgroundColor: symbol.background } : undefined}
    >
      <img src={symbol.url} alt="" />
    </span>
  )
}

type ConsentModalProps = BrandProps & {
  isSubmitting: boolean
  onCancel: () => void
  onConfirm: () => void
}

/**
 * 이용 신청 — 아직 권한이 없는 계정이 배너·드롭다운을 눌렀을 때.
 *
 * 거절 버튼은 "닫기" 가 아니라 **취소**다. 결과가 생기는 확인형 질문이라서다.
 * 주행동이 도착("이용")이 아니라 요청("신청")인 것은, 학원당 원장이 하나뿐이라
 * 이 버튼을 누르는 다수가 소속 선생님이고 그들은 바로 뒤에 관리자 요청 모달을 보기 때문이다.
 */
export const ConsentModal = ({ brand, isSubmitting, onCancel, onConfirm }: ConsentModalProps) => {
  const copy = SERVICE_ENTRY_COPY[brand]

  return (
    <div css={S.modal} role="dialog" aria-label={copy.consentTitle}>
      <EnglishflatMark brand={brand} />
      <p css={S.title}>{copy.consentTitle}</p>
      <p css={S.body}>
        {copy.consentLeadBefore}
        {copy.consentLeadEmphasis && <b>{copy.consentLeadEmphasis}</b>}
        {copy.consentLeadAfter}
        <br />
        {copy.consentQuestion}
      </p>
      <div css={S.buttons}>
        <button type="button" css={S.button} onClick={onCancel} disabled={isSubmitting}>
          취소
        </button>
        <button type="button" css={S.ctaButton} onClick={onConfirm} disabled={isSubmitting}>
          {/* 신청은 서버 왕복이라 진행 중임을 버튼 안에서 보여준다. 폭이 고정(120)이라 이동이 없다. */}
          {isSubmitting ? <Loader theme="white" width={20} /> : '원클릭 신청하기'}
        </button>
      </div>
    </div>
  )
}

/**
 * 관리자 요청 — 소속 선생님이 신청을 누른 뒤.
 *
 * "권한이 없습니다" 로 열지 않는다. 첫 줄부터 부정이면 앞 모달의 CTA 가 거절로 되받아쳐진다.
 * 길이 막힌 게 아니라 한 단계 남은 것이라고 먼저 말한다.
 * 절차 두 줄을 산문 한 줄로 접지 않는다 — 두 단계라는 것과 계정 층위가 같이 사라진다.
 * 과목 권한을 주는 자리는 매쓰플랫 학원관리가 아니라 영어 쪽 '선생님 관리' 다.
 */
export const AdminRequestModal = ({ brand, onClose }: BrandProps & { onClose: () => void }) => {
  const copy = SERVICE_ENTRY_COPY[brand]

  return (
    <div css={S.adminModal} role="alertdialog" aria-label="관리자 선생님께 이용 요청">
      <p css={[S.body, S.leadBody]}>{copy.adminLead}</p>
      <ol css={S.steps}>
        {copy.adminSteps.map((step, index) => (
          <li key={step}>
            <span css={S.stepNo}>{index + 1}.</span>
            {step}
          </li>
        ))}
      </ol>
      <div css={S.buttons}>
        <button type="button" css={S.ctaButton} onClick={onClose}>
          확인
        </button>
      </div>
    </div>
  )
}

/**
 * 신청 완료 — 신청이 받아들여진 뒤.
 *
 * 보조 버튼은 "취소" 가 아니라 **닫기**다. 신청이 이미 끝나 되돌릴 게 없고, 되물을 것도 없다.
 * 남은 선택은 지금 갈지 말지뿐이다.
 * 아이콘 색은 우리 보라를 쓴다 — 남의 앱 지면이지만 우리 서비스가 열렸다는 신호다.
 */
export const DoneModal = ({
  brand,
  onClose,
  onConfirm,
}: BrandProps & { onClose: () => void; onConfirm: () => void }) => {
  const copy = SERVICE_ENTRY_COPY[brand]

  return (
    <div css={S.modal} role="alertdialog" aria-label={copy.doneTitle}>
      <Icon name="check_circle" size={36} color="var(--Colors-Violet-400)" />
      <p css={S.title}>{copy.doneTitle}</p>
      <p css={S.body}>
        {copy.doneLead}
        <br />
        {copy.doneAccount}
      </p>
      <div css={S.buttons}>
        <button type="button" css={S.button} onClick={onClose}>
          닫기
        </button>
        <button type="button" css={S.ctaButton} onClick={onConfirm}>
          바로가기
        </button>
      </div>
    </div>
  )
}

/**
 * 이미 신청됨 — 신청한 뒤 화면이 갱신되기 전에 다시 눌렀을 때.
 *
 * 경고가 아니라 제안이다. 이미 쓸 수 있는 계정이라 사용자가 잘못한 게 없다.
 * 그래서 caution 아이콘·빨강·확인 하나를 쓰지 않고 신청 모달과 같은 상자로 맞춘다.
 * 제목은 능동 높임이다 — "신청되었습니다" 로 쓰면 본인이 누른 일인데 남이 한 것처럼 읽힌다.
 */
export const DupeModal = ({
  brand,
  onCancel,
  onConfirm,
}: BrandProps & { onCancel: () => void; onConfirm: () => void }) => {
  const copy = SERVICE_ENTRY_COPY[brand]

  return (
    <div css={S.modal} role="dialog" aria-label={copy.dupeTitle}>
      <EnglishflatMark brand={brand} />
      <p css={S.title}>{copy.dupeTitle}</p>
      <p css={S.body}>
        {copy.dupeLead}
        <br />
        {copy.dupeQuestion}
      </p>
      <div css={S.buttons}>
        <button type="button" css={S.button} onClick={onCancel}>
          취소
        </button>
        <button type="button" css={S.ctaButton} onClick={onConfirm}>
          바로가기
        </button>
      </div>
    </div>
  )
}
